import { Injectable, OnDestroy } from "@angular/core";
import { Subject, BehaviorSubject, fromEvent } from "rxjs";
import { takeUntil, debounceTime, filter } from "rxjs/operators";
import { Router, NavigationEnd } from "@angular/router";
import { AuthService } from './auth.service';
// Menu
export interface Menu {
  headTitle1?: string;
  headTitle2?: string;
  path?: string;
  title?: string;
  icon?: string;
  type?: string;
  badgeType?: string;
  badgeValue?: string;
  active?: boolean;
  bookmark?: boolean;
  children?: Menu[];
  hidden?: boolean;
  permiso?: string; // Permiso necesario
  rol?: string; // Rol necesario
  bold?: boolean; // Mostrar el texto en negrita
}

@Injectable({
  providedIn: "root",
})
export class NavService implements OnDestroy {
  private unsubscriber: Subject<any> = new Subject();
  public screenWidth: BehaviorSubject<number> = new BehaviorSubject(window.innerWidth);
  public items = new BehaviorSubject<Menu[]>([]);
 
  // Responsive breakpoints
  private readonly MOBILE_BREAKPOINT = 768;
  private readonly TABLET_BREAKPOINT = 991;
  private readonly DESKTOP_BREAKPOINT = 1199;

  // Device detection
  public isMobile: BehaviorSubject<boolean> = new BehaviorSubject(this.checkIfMobile());
  public isTablet: BehaviorSubject<boolean> = new BehaviorSubject(this.checkIfTablet());
  public isTouchDevice: boolean = this.checkTouchDevice();

  // Search Box
  public search: boolean = false;

  // Language
  public language: boolean = false;

  // Mega Menu
  public megaMenu: boolean = false;
  public levelMenu: boolean = false;
  public megaMenuColapse: boolean = window.innerWidth < this.DESKTOP_BREAKPOINT;

  // Collapse Sidebar
  public collapseSidebar: boolean = window.innerWidth < this.TABLET_BREAKPOINT;

  // For Horizontal Layout Mobile
  public horizontal: boolean = window.innerWidth >= this.TABLET_BREAKPOINT;

  // Full screen
  public fullScreen: boolean = false;

  constructor(private router: Router, private auth: AuthService) {
    this.setScreenWidth(window.innerWidth);
    this.updateResponsiveSettings(window.innerWidth);

    // Detectar cambios de tamaño con debounce optimizado
    // Menor debounce en mobile para mejor respuesta táctil
    const debounceDelay = this.isTouchDevice ? 200 : 300;
    
    fromEvent(window, "resize")
      .pipe(debounceTime(debounceDelay), takeUntil(this.unsubscriber))
      .subscribe((evt: any) => {
        const width = evt.target.innerWidth;
        this.setScreenWidth(width);
        this.updateResponsiveSettings(width);
      });

    // Detectar cambio de orientación en mobile
    if (this.isTouchDevice) {
      fromEvent(window, "orientationchange")
        .pipe(debounceTime(100), takeUntil(this.unsubscriber))
        .subscribe(() => {
          setTimeout(() => {
            const width = window.innerWidth;
            this.setScreenWidth(width);
            this.updateResponsiveSettings(width);
          }, 200);
        });
    }

    // 🔹 Filtrar menú cuando cambie el usuario autenticado
    this.auth.currentUser.subscribe(() => {
      this.actualizarMenuFiltrado();
    });

    // 🔹 Cerrar menú automáticamente al navegar (solo en mobile)
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.unsubscriber)
      )
      .subscribe(() => {
        this.onNavigate();
      });
  }
  ngOnDestroy() {
    this.unsubscriber.next(null);
    this.unsubscriber.complete();
  }

  private setScreenWidth(width: number): void {
    this.screenWidth.next(width);
  }

  // 🔹 Detectar dispositivos móviles
  private checkIfMobile(): boolean {
    return window.innerWidth < this.MOBILE_BREAKPOINT;
  }

  // 🔹 Detectar tablets
  private checkIfTablet(): boolean {
    const width = window.innerWidth;
    return width >= this.MOBILE_BREAKPOINT && width < this.TABLET_BREAKPOINT;
  }

  // 🔹 Detectar dispositivos táctiles
  private checkTouchDevice(): boolean {
    return ('ontouchstart' in window) || 
           (navigator.maxTouchPoints > 0) || 
           (navigator.maxTouchPoints > 0);
  }

  // 🔹 Actualizar configuraciones responsive
  private updateResponsiveSettings(width: number): void {
    const wasMobile = this.isMobile.value;
    const wasTablet = this.isTablet.value;

    // Actualizar estados de dispositivo
    this.isMobile.next(width < this.MOBILE_BREAKPOINT);
    this.isTablet.next(width >= this.MOBILE_BREAKPOINT && width < this.TABLET_BREAKPOINT);

    // Actualizar menú según tamaño
    if (width < this.TABLET_BREAKPOINT) {
      this.collapseSidebar = true;
      this.horizontal = false;
    } else {
      this.collapseSidebar = false;
      this.horizontal = true;
    }

    // Actualizar mega menú
    this.megaMenuColapse = width < this.DESKTOP_BREAKPOINT;

    // Cerrar paneles abiertos al cambiar de mobile a desktop y viceversa
    if ((wasMobile && !this.isMobile.value) || (!wasMobile && this.isMobile.value)) {
      this.search = false;
      this.language = false;
      this.megaMenu = false;
      this.levelMenu = false;
    }
  }

  // 🔹 Toggle sidebar (optimizado para mobile)
  public toggleSidebar(): void {
    this.collapseSidebar = !this.collapseSidebar;
    // No cerrar otros paneles al hacer toggle del sidebar
  }

  // 🔹 Cerrar sidebar (útil en mobile al navegar)
  public closeSidebar(): void {
    if (this.isMobile.value || this.isTablet.value) {
      this.collapseSidebar = true;
    }
  }

  // 🔹 Método llamado al navegar - cierra menú solo en mobile
  public onNavigate(): void {
    if (this.isMobile.value) {
      this.collapseSidebar = true;
      // Solo cerrar paneles auxiliares, no afectar el sidebar
      this.search = false;
      this.language = false;
      this.megaMenu = false;
      this.levelMenu = false;
    }
  }

  // 🔹 Cerrar menú al seleccionar opción (para uso manual si es necesario)
  public closeMenuOnSelect(): void {
    if (this.isMobile.value) {
      this.collapseSidebar = true;
    }
  }

  // 🔹 Abrir sidebar
  public openSidebar(): void {
    this.collapseSidebar = false;
  }

  // 🔹 Toggle search con mejor manejo en mobile
  public toggleSearch(): void {
    this.search = !this.search;
    if (this.search && this.isMobile.value) {
      // Cerrar otros paneles en mobile, pero no el sidebar
      this.language = false;
      this.megaMenu = false;
      this.levelMenu = false;
    }
  }

  // 🔹 Toggle language
  public toggleLanguage(): void {
    this.language = !this.language;
    if (this.language && this.isMobile.value) {
      // Cerrar otros paneles en mobile, pero no el sidebar
      this.search = false;
      this.megaMenu = false;
      this.levelMenu = false;
    }
  }

  // 🔹 Toggle mega menu
  public toggleMegaMenu(): void {
    this.megaMenu = !this.megaMenu;
    if (this.megaMenu && this.isMobile.value) {
      // Cerrar otros paneles en mobile
      this.search = false;
      this.language = false;
    }
  }

  // 🔹 Cerrar todos los paneles auxiliares (no afecta el sidebar principal)
  public closeAllPanels(): void {
    this.search = false;
    this.language = false;
    this.megaMenu = false;
    this.levelMenu = false;
  }

  MENUITEMS: Menu[] = [
    {
      headTitle1: "General",
    },

    {
      title: "Tablero",
      icon: "home",
      type: "link",
      path: "/dashboard/default",
      permiso: "dashboard.ver",
      active: true,
    },
    /*  {
      title: "",
      icon: "home",
      type: "sub",
      badgeType: "light-primary",
      badgeValue: "5",
      active: true,
      children: [
        { path: "/dashboard/default", title: "Tablero", type: "link" },
       // { path: "/dashboard/ecommerce", title: "Ecommerce", type: "link" },
       // { path: "/dashboard/online-course", title: "Online course", type: "link" },
       // { path: "/dashboard/crypto", title: "Crypto", type: "link" },
       // { path: "/dashboard/social", title: "Social", type: "link" }, 
      ],
    },
    {
      title: "Widgets",
      icon: "widget",
      type: "sub",
      active: false,
      children: [
        { path: "/widgets/general", title: "General", type: "link" },
        { path: "/widgets/chart", title: "Chart", type: "link" },
      ],
    },  */
   /*  {
      
      headTitle1: "Módulos",
      headTitle2: "Ready To Use Apps.",
    }, */
    {
      title: "Administración",
      icon: "faq",
      type: "sub",
      badgeType: "light-secondary",
      bold: true,
      //badgeValue:"NUEVO",
      active: false,
      permiso: "administracion.ver",
      children: [
        { path: '/administracion/configurar', title: 'Configurar', type: 'link', permiso: 'administracion.configurar' },      
        
      ],
    },
{
  
  title: "Personas",
  icon: "user",
  type: "sub",
  //badgeType: "light-secondary",
  //badgeValue: "Mejorado",
  active: false,
  permiso: "personas.ver",
  bold: true,
  children: [
    { path: '/personas/nueva', title: 'Nuevo', type: 'link', permiso: 'personas.crear' },

    {
      title: 'Listados',
      icon: 'box',
      type: 'sub',
      permiso: 'personas.listar',
      children: [
        { path: '/personas/listados/activos', title: 'Activos', type: 'link', permiso: 'personas.listado_activos' },
        { path: '/personas/listados/inactivos', title: 'Inactivos', type: 'link', permiso: 'personas.listado_inactivos' },                  
      ],
    },

    { path: '/licencia/licencias', title: 'Licencias', type: 'link', permiso: 'personas.licencias' },
    { path: '/multas/multas', title: 'Multas', type: 'link', permiso: 'personas.multas' },
    { path: '/cumpleanos/cumpleanos', title: 'Cumpleaños', type: 'link', permiso: 'personas.cumpleanios' },
    { path: '/cargos/listados', title: 'Cargos', type: 'link', permiso: 'personas.cargos.listar' },
    
        {
          title: 'Capacitaciones',
          icon: 'box',
          type: 'sub',
          permiso: '',
          children: [
              { path: '/capacitacion-catalogo/capacitacion-catalogo', title: 'Catalogo', type: 'link', permiso: 'personas.capacitaciones.catalogo' },
              { path: '/capacitacion-programacion/capacitacion-programacion', title: 'Programación', type: 'link', permiso: 'personas.capacitaciones.listar' },
          ],
        },

{
  title: "Exámenes Médicos",
  icon: "alert-triangle",
  type: "sub",
 // badgeType: "light-secondary",
 // badgeValue: "Mejorado",
  permiso: "personas.examenes_medicos.ver",
  children: [

    {
      title: 'Clasificaciones',
      type: 'link',
      path: '/clasificaciones/listados',
      permiso: 'personas.clasificaciones.listar'
    },
    {
      title: 'Peligros',
      type: 'link',
      path: '/peligros/listados',
      permiso: 'personas.peligros.listar'
    },
    {
      title: 'Tipos Examen',
      type: 'link',
      path: '/tipos-examen/listados',
      permiso: 'personas.tipos_examen.listar'
    },
    {
      title: 'Exámenes',
      type: 'link',
      path: '/examenes/listados',
      permiso: 'personas.examenes.listar'
    },
    
        {
          title: 'Profesiogramas',
          type: 'link',
          path: '/profesiogramas/listados',
          permiso: 'personas.profesiogramas.listar'
        },
        {
          title: 'Profe. Exámenes',
          type: 'link',
          path: '/profesiograma-examenes/listados',
          permiso: 'personas.profesiograma_examenes.listar'
        },
        {
          title: 'Profe. Peligros',
          type: 'link',
          path: '/profesiograma-peligros/listados',
          permiso: 'personas.profesiograma_peligros.listar'
        },
        {
          title: 'Profe. Cargos',
          type: 'link',
          path: '/profesiograma-cargos/listados',
          permiso: 'personas.profesiograma_cargos.listar'
        },

    // 👇 ESTE ES EL NIVEL CORRECTO
    {
      title: "Profesiogramas",
      icon: "box",
      type: "sub",
      permiso: "personas.profesiogramas.ver",
      children: [


      ],
    },
  ],
}

  ],
},
    {
      title: "Empresarial",
      // badgeValue:"MEJORADO",
      icon: "ecommerce",
      type: "sub",
      badgeType: "light-success",        
      active: false,
      permiso: "empresarial.ver",
      bold: true,
      children: [
        {
          title: "Empresas",
          icon: "box",
          type: "sub",
          children: [
            {
              path: "/nueva-empresa/nueva-empresa",
              title: "Nueva",
              type: "link",
              permiso: "empresarial.crear",
            },
            {
              path: "/empresas-activas/empresas-activas",
              title: "Activas",
              type: "link",
              permiso: "empresarial.listar_activas",
            },
            {
              path: "/empresas-inactivas/empresas-inactivas",
              title: "Inactivas",
              type: "link",
              permiso: "empresarial.listar_inactivas",
            },
          ],
        },
         {
           title: "Encuestas",
           icon: "box",
           type: "sub",
           permiso: "empresarial.encuestas",
          
           children: [
             { path: "/encuestas/nueva", title: "Nueva", type: "link", permiso: "empresarial.encuestas.crear" },
          { path: "/encuestas", title: "Listado", type: "link", permiso: "empresarial.encuestas.listar" },
            
            ],
         },
      ],
    },
    {
      title: "Vehiculos",
      icon: "veh",
      type: "sub",
      bold: true,
    //  badgeValue:"Mejorado",
    //  badgeType: "light-secondary",
      active: false,
      permiso: "vehiculos.ver",
      children: [
        {
          path: "/vehiculos/nuevos",
          title: "Nuevo",
          type: "link",
          permiso: "vehiculos.crear",
        },
        {
          title: "Listados",
          icon: "box",
          type: "sub",
          children: [
            {
              path: "/afiliados-activos/afiliados-activos",
              title: "Afiliados activos",
              type: "link",
              permiso: "vehiculos.listar_activas",
            },
            {
              path: "/terceros-activos/terceros-activos",
              title: "Terceros activos",
              type: "link",
              permiso: "vehiculos.listarTerceros",
            },
            {
              path: "/inactivos/inactivo",
              title: "Inactivos",
              type: "link",
              permiso: "vehiculos.listar_inactivas",
            },
            {
              path: "/particular/particular",
              title: "Particular",
              type: "link",
              permiso: "vehiculos.listarParticulares",
            },
            {
              path: "/general/general",
              title: "General",
              type: "link",
              permiso: "vehiculos.listaGeneral",
            },
          ],
        },
        {
          title: "Marcas",
          icon: "box",
          type: "sub",
          permiso: "vehiculos.marcas",
          children: [
            {
              path: "/listado-marcas/listado-marcas",
              title: "Activas",
              type: "link",
              permiso: "vehiculos.listaMarcas",
            },
            {
              path: "/marcas-inactivas/marcas-inactivas",
              title: "Inactivas",
              type: "link",
              permiso: "vehiculos.listaMarcasInactivas",
            },
          ],
        },
        {
          title: "Modelos",
          icon: "box",
          type: "sub",
          permiso: "vehiculos.modelos",
          children: [
            {
              path: "/listado-modelo/listado-modelo",
              title: "Activos",
              type: "link",
              permiso: "vehiculos.listaModelos",
            },
            {
              path: "/modelo-inactivo/modelo-inactivo",
              title: "Inactivos",
              type: "link",
              permiso: "vehiculos.listaModelosInactivos",
            },
          ],
        },
        
        {
          title: "Rodamientos",
          icon: "check",
          type: "sub",
          
          permiso: "vehiculos.rodamientos.ver",
          children: [
            { path: "/rodamientos/configurar", title: "Configurar", type: "link", permiso: "vehiculos.rodamientos.configurar" },
            { path: "/rodamientos/pagos", title: "Pagos", type: "link", permiso: "vehiculos.rodamientos.pagos" },
            { path: "/rodamientos/reportes", title: "Reportes", type: "link", permiso: "vehiculos.rodamientos.reportes" },
          ],
        },
        {
          path: "/documentos/documentos",
          title: "Documentos",
          type: "link",
          permiso: "vehiculos.documentos",
        },
        {
          path: "/alistamientos-diarios/alistamientos-diarios",
          title: "Alistamientos Diarios",
          type: "link",
          permiso: "vehiculos.alistamientos",
        },
         {
          title: "Siniestros Viales",
          icon: "check",
          type: "sub",
          
          permiso: "vehiculos.rodamientos.ver",
          children: [
            { path: "/Listado-siniestros-viales/Listado-siniestros-viales", title: "Listado Siniestros", type: "link", permiso: "vehiculos.Listado_siniestros_viales" },
            { path: "/siniestros-viales-crear/siniestros-viales-crear", title: "Crear Siniestro Vial", type: "link", permiso: "vehiculos.siniestros_viales_crear" },
          ],

        },
      //  { path: "/alistamientos-diarios/alistamientos-diarios", title: "Alistamientos Diarios", type: "link",permiso: "vehiculos.alistamientos" },
       { path: "/vehiculos/actualizar-polizas", title: "Actualizar Pólizas", type: "link", permiso: "vehiculos.actualizar_polizas" },
        {
          path: "/cartas/cartas",
          title: "Cartas",
          type: "link",
          permiso: "vehiculos.cartas",
        },
        {
          path: "/conductores-asignados/conductores-asignados",
          title: "Conductores Asignados",
          type: "link",
          permiso: "vehiculos.conductores",
        },
        //{ path: "/polizas/polizas", title: "Actualizar Pólizas", type: "link" },
        //{ path: "/planos/planos", title: "Generar Planos TO", type: "link" },
      ],
    },

    {
      title: "Extractos",
      icon: "file",
      type: "sub",
      badgeType: "light-secondary",
      active: false,
      permiso: "extractos.ver",
      bold: true,
      children: [
        {
          path: "/elaborar/elaborar",
          title: "Elaborar",
          type: "link",
          permiso: "extractos.elaborar",
        },
        {
          path: "/listado-extractos/listado-extractos",
          title: "Listado",
          type: "link",
          permiso: "extractos.listado",
        },
        {
          path: "/consultar/consultar",
          title: "Consultar",
          type: "link",
          permiso: "extractos.consultar",
        },
        {
          path: "/contratos/contratos",
          title: "Contratos",
          type: "link",
          permiso: "extractos.contratos",
        },
        {
          title: "Rutas",
          icon: "box",
          type: "sub",
          permiso: "extractos.rutas",
          children: [
            {
              path: "/listado-rutas/listado-rutas",
              title: "Listado",
              type: "link",
            },
            {
              path: "/rutas-inactivas/rutas-inactivas",
              title: "Inactivas",
              type: "link",
            },
          ],
        },
        {
          title: "Departamentos",
          icon: "box",
          type: "sub",
          permiso: "extractos.departamentos",
          children: [
            {
              path: "/listado-departamento/listado-departamento",
              title: "Listado",
              type: "link",
            },
            {
              path: "/departamentos-inactivos/departamentos-inactivos",
              title: "Inactivos",
              type: "link",
            },
          ],
        },
      ],
    },
    {
      title: "PESV",
      icon: "blog",
      type: "sub",
      badgeType: "light-success",
      badgeValue:"Nuevo",
      active: false,
      bold: true,
      permiso: "pesv.ver",
      children: [
        {
          title: "Fase 1 Planificación",
          icon: "box",
          type: "sub",
          permiso: "extractos.rutas",
          children: [
            {
              path: "/registrar-pesv/registrar-pesv",
              title: "1. Líder PESV",
              type: "link",
              permiso: "pesv.ver",
            },
            {
              path: "/comite/comite",
              title: "2. Comité PESV-COPASST",
              type: "link",
              permiso: "pesv.ver",
            },
            
             {
              path: "/politica-seguridad-vial/politica-seguridad-vial",
              title: "3. Política de Seguridad Vial",
              type: "link",
              permiso: "pesv.ver",
            },
              {
              path: "/liderazgo-compromiso/liderazgo-compromiso",
              title: "4. Liderazgo y Compromiso",
              type: "link",
              permiso: "pesv.ver",
            },
                            {
              path: "/diagno",
              title: "5. Diagnóstico",
              icon: "box",
              type: "sub",
              permiso: "extractos.rutas",
              children: [
                {
                  path: "/diagnostico-pesv/sede",
                  title: "Sede",
                  type: "link",
                  permiso: "pesv.ver",
                },
                {
                  path: "/diagnostico-pesv/colaboradores",
                  title: "Colaboradores",
                  type: "link",
                  permiso: "pesv.ver",
                },
                {
                  path: "/diagnostico-pesv/contratista",                 
                  title: "Contratistas",
                  type: "link",
                  permiso: "pesv.ver",
                },
                {
                  path: "/diagnostico-pesv/vehiculos",                 
                  title: "Vehiculos",
                  type: "link",
                  permiso: "pesv.ver",
                },
                {
                  path: "/diagnostico-pesv/identificar_rutas",
                  title: "Identificar Rutas",
                  type: "link",
                  permiso: "pesv.ver",
                },
                {
                  path: "/diagnostico-pesv/emergencia",
                  title: "Emergencia PESV",
                  type: "link",
                  permiso: "pesv.ver",
                },
                
              ],
            }, 
            
            {
              path: "/riesgos-viales/riesgos-viales",
              title: "6. Riesgos Viales",
              type: "link",
              permiso: "pesv.ver",
            },

            {
              title: "7. Objetivos y Metas del PESV",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [
                {
              path: "/objetivo-metas",
              title: "Objetivos y Metas",
              type: "link",
              permiso: "pesv.ver",
            },
                {
                  path: "/listado/metas",
                  title: "Metas Indicadores",
                  type: "link",
                  permiso: "pesv.ver",
                },
              ],
            }, 
            {
              title: "8. Programas y gestión de riesgos",
              icon: "box",
              type: "sub",
              permiso: "extractos.rutas",
              children: [
                {
                  path: "/listado/programas-gestion",
                  title: "Programa PESV",
                  type: "link",
                  permiso: "pesv.ver",
                },
                
                {
                  path: "/listado/tipos-programa",
                  title: "Tipos de Programa",
                  type: "link",
                  permiso: "pesv.ver",
                },
               
            
            
                
              ],
            }, 
            /*
            {
              path: "/liderazgo-compromiso/liderazgo-compromiso",
              title: "4. Liderazgo y Compromiso",
              type: "link",
              permiso: "personas.listado_inactivos",
            },
            tista",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/diagno{
              title: "5. Diagnóstico",
              icon: "box",
              type: "sub",
              permiso: "extractos.rutas",
              children: [
                {
                  path: "/diagnostico-pesv/sede",
                  title: "Sede",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/diagnostico-pesv/colaboradores",
                  title: "Colaboradores",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/diagnostico-pesv/contratista",
                  title: "Contrastico-pesv/vehiculos",
                  title: "Vehiculos",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/diagnostico-pesv/identificar_rutas",
                  title: "Identificar Rutas",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/diagnostico-pesv/emergencia",
                  title: "Emergencia PESV",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
              ],
            },
            {
              path: "/riesgos-viales/riesgos-viales",
              title: "6. Riesgos Viales",
              type: "link",
              permiso: "personas.listado_inactivos",
            },
            {
              path: "/objetivo-metas",
              title: "7. Objetivos y Metas del PESV",
              type: "link",
              permiso: "personas.listado_inactivos",
            },
            {
              title: "8. Programas y gestión de riesgos",
              icon: "box",
              type: "sub",
              permiso: "extractos.rutas",
              children: [
                {
                  path: "/programa-pesv/programa-pesv",
                  title: "Programa PESV",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/programas-gestion/equipo-medicion",
                  title: "Equipo de medición",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/programas-gestion/control-velocidad",
                  title: "Control de velocidad",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/programas-gestion/casos-reiterativos-pesv",
                  title: "Casos reiterativos PESV",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/programas-gestion/campanas-prevencion-pesv",
                  title: "Campañas prevención PESV",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
                {
                  path: "/programas-gestion/campanas-prevencion-pesv",
                  title: "Campañas prevención PESV",
                  type: "link",
                  permiso: "personas.listado_inactivos",
                },
              ],
            }, */
          ],
        },

         {
          title: "Fase 2 Implementación",
          icon: "box",
          type: "sub",
          permiso: "pesv.ver",
          children: [
{
              title: "9. Plan Anual",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [
                { path: "/plan-anual/plan-anual",title: "Crear Plan Anual", type: "link", permiso: "pesv.ver" },
                { path: "/gestion-plan/gestion-plan",title: "Gestión del Plan", type: "link", permiso: "pesv.ver" },
              ],
            },
            {
              title: "10. Competencias y Planes",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [

                { path: "documentos-competencia/documentos-competencia", title: "Documentos", type: "link", permiso: "pesv.ver" },
                { path: "/competencias/competencias",title: "Competencias", type: "link", permiso: "pesv.ver" },
                { path: "/gestion-competencias/gestion-competencias",title: "Gestión de Competencias", type: "link", permiso: "pesv.ver" },
                { path: "/plan-competencia-anual/plan-competencia-anual",title: "Plan de Competencia Anual", type: "link", permiso: "pesv.ver" }
                
              ],
            },
             {
              title: "11. Responsabilidad Comportamiento",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [

                { path: "/listado/procedimiento-contratacion",title: "Procedimiento Contratación", type: "link", permiso: "pesv.ver" },
                { path: "/listado/requisitos",title: "Requisitos", type: "link", permiso: "pesv.ver" },
                { path: "/listado/quejas",title: "Quejas", type: "link", permiso: "pesv.ver" },
                { path: "/listado/responsabilidades",title: "Responsabilidades", type: "link", permiso: "pesv.ver" },
                { path: "/listado/criterios",title: "Criterios", type: "link", permiso: "pesv.ver" },
                { path: "/listado/evaluacion",title: "Evaluación", type: "link", permiso: "pesv.ver" },
                { path: "/listado/tipo-comportamiento",title: "Tipo de Comportamiento", type: "link", permiso: "pesv.ver" },
                { path: "/listado/aspectos-comportamiento",title: "Aspectos de Comportamiento", type: "link", permiso: "pesv.ver" },
                { path: "/listado/seguimiento-comportamiento-vial",title: "Seguimiento de Comportamiento", type: "link", permiso: "pesv.ver" },

              ],
            }, 
            {
              title: "12. PPRAEV",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [

                { path: "/listado/documentos-plan-respuesta",title: "Documentos plan", type: "link", permiso: "pesv.ver" },
              ],
            }, 
            {
              title: "13. Investigación Interna de Siniestros",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [

                { path: "/listado/documentos-investigacion", title: "Documentos Investigación", type: "link", permiso: "pesv.ver" },
                { path: "/listado/investigacion", title: "Investigación Interna de Siniestros",  type: "link", permiso: "pesv.ver"},
                
              ],
            }, 
            {
              path: '/inspeccion-vehiculos/inspeccion-vehiculos',
              title: '16. Inspección de Vehículos',
              type: 'link',
              permiso: 'pesv.ver'
            },
            // Vias Seguras 
           /*  {
              title: "14. Vias seguras",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [
                { path: "/listado/documentos-vias", title: "Documentos Vías", type: "link", permiso: "pesv.ver" },
                { path: "/listado/vias-internas", title: "Listado Vías Internas", type: "link", permiso: "pesv.ver" },
                { path: "/listado/vias-zonas", title: "Listado Zonas Vías", type: "link", permiso: "pesv.ver" }
              ],
            },
            {
              title: "15. Planificación de desplazamientos",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [
                { path: "/listado/documentos-planificacion", title: "Documentos Planificación", type: "link", permiso: "pesv.ver" },
                { path: "/listado/desplazamientos", title: "Desplazamientos", type: "link", permiso: "pesv.ver" }
              ],
            },
            
            {
              title: "18. Cambio y gestion de contratista",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [
                { path: "/listado/documentos-gestion-cambio", title: "Documentos Gestión de Cambio", type: "link", permiso: "pesv.ver" }
              ],
            },
            {
              title: "19. Archivo y retención",
              icon: "box",
              type: "sub",
              permiso: "pesv.ver",
              children: [
                { path: "/registrar-retencion", title: "Registrar Retención", type: "link", permiso: "pesv.ver" },
                { path: "/listado/retencion", title: "Archivo y Retención", type: "link", permiso: "pesv.ver" }
              ],
            }, */
              {
              path: '/mantenimientos-vehiculos',
              title: '17. Mantenimientos Vehículos',
              type: 'link',
              permiso: 'pesv.ver'
            }
              
              
          ]    

           
                            }, 
      ],
    },
  
    {
      title: "Mantenimientos",
      icon: "to-do",
      type: "sub",
      // badgeType: "light-secondary",
      // badgeValue: "Mejorado",
      badgeType: "light-secondary",
      active: false,
      permiso: "mantenimientos.ver",
      bold: true,
      
      children: [
               {
          title: "Gestión",
          icon: "box",
          type: "sub",
          permiso: "mantenimientos.ver",
          children: [
             {
          path: "/registrar/registrar",
          title: "Registrar",
          type: "link",
          permiso: "mantenimientos.registrar",
        },
        {
          path: "/listado-mantenimientos/listado-mantenimientos",
          title: "Consultar",
          type: "link",
          permiso: "mantenimientos.listados",
        },

        {
          path: "/listado/programacion-mantenimientos",
          title: "Programaciones",
          type: "link",
          permiso: "mantenimientos.listados",
        },
         {
          path: "/ordenes/ordenes",
          title: "Ordenes",
          type: "link",
          permiso: "mantenimientos.listados",
        },
           
            
          ],
        },
         {
          title: "Combustibles",
          icon: "box",
          type: "sub",
          permiso: "mantenimientos.ver",
          children: [
            {
              path: "/combustible/combustible",
              title: "Registrar",
              type: "link",
              permiso: "mantenimientos.combustible",
            },

             {
               path: "/consultar-combustible/consultar-combustible",
               title: "Consultar",
               type: "link",
               permiso: "mantenimientos.consultarCombustible",
            },
           
            
          ],
        },
        
        {
          title: "Plan de Mantenimientos",
          icon: "box",
          type: "sub",
          permiso: "extractos.departamentos",
          children: [
            {
              path: "/mantenimientos/crear-plan-mantenimientos",
              title: "Registrar Plan",
              type: "link",
            },

             {
              path: "/mantenimientos/listar-plan-mantenimientos",
              title: "Listar Planes",
              type: "link",
            },
           
            
          ],
        },

        /*   { path: "/ordenes/ordenes", title: "Ordenes", type: "link" }, */
      ],
    },
{
      title: "GPS",
      icon: "maps",
      type: "sub",     
      active: false,
      permiso: "gps.ver",
      bold: true,
      children: [
      
       { path: "/reportes-gps", title: "Reportes", type: "link", permiso: "gps.reportes" },
         { path: "/certificados-gps", title: "Certificados GPS", type: "link", permiso: "gps.certificados_gps" },
      ],
    },
    {
      title: "Notificaciones",
      icon: "bell",
      type: "sub",
      badgeType: "light-secondary",
      permiso: "notificaciones.ver",
      bold: true,
      active: false,

      children: [
       /*  {
          path: "/enviar/enviar",
          title: "Enviar notificaciones",
          type: "link",
          permiso: "notificaciones.enviar",
        }, */
        {
          path: "/configuracion-notificaciones",
          title: "Configurar",
          type: "link",
          permiso: "notificaciones.enviar",
        }, 
      ],
    },

  /*   {
      title: "Pesv digitales",
      icon: "blog",
      type: "sub",
      badgeType: "light-secondary",
      active: false,
      permiso: "extractos.ver",
      children: [
        {
          path: "/pesv-digitales/pesv-digitales",
          title: "Digitales PESV",
          type: "link",
          permiso: "personas.listado_inactivos",
        },
      ],
    },
 */
    /*{
    /* {
      title: "PESV",
      icon: "bell",
      type: "sub",
      badgeType: "light-secondary",
      permiso: "notificaciones.ver",
      active: false,
      
      children: [
        /* {
          title: 'Planificación',
          icon: 'box',
          type: 'sub',
          permiso: 'personas.listar',
          children: [
              { path: '/personas/listados/activos', title: 'Activos', type: 'link', permiso: 'personas.listado_activos' },
              { path: '/personas/listados/inactivos', title: 'Inactivos', type: 'link', permiso: 'personas.listado_inactivos' },
          ],
        }, */
        
      
    {
      title: "Usuarios",
      icon: "user",
      type: "sub",
      bold: true,
      permiso: "usuarios.ver",
       badgeType: "light-success",
       //badgeValue:"NUEVO",
      active: false,
      children: [
     //   { path: "/usuarios-app/usuarios-app", title: "Usuarios App", type: "link" },
        { path: "/web/web", title: "Usuarios Web", type: "link",  permiso: "usuarios.Listar" },
       // { path: "/ejecutadas/ejecutadas", title: "Tareas Ejecutadas", type: "link" },
      ],
    },
    
  ];

  // 🔹 Filtrar menú según permisos y roles del usuario
// 🔹 Filtrar menú según permisos y roles del usuario
  private filtrarMenuPorPermisos(menu: Menu[]): Menu[] {
    return menu
      .filter(item => {
        // Si tiene restricción por rol o permiso, validar
        if (item.rol && !this.auth.hasRol(item.rol)) return false;
        if (item.permiso && !this.auth.hasPermiso(item.permiso)) return false;
        return true;
      })
      .map(item => {
        if (item.children) {
          item.children = this.filtrarMenuPorPermisos(item.children);
        }
        return item;
      });
  }

  // 🔹 Actualiza el menú visible
  actualizarMenuFiltrado() {
    const filtrado = this.filtrarMenuPorPermisos(this.MENUITEMS);
    this.items.next(filtrado);
   // console.log('Menú actualizado:', filtrado);
  }
  // Array
  // items = new BehaviorSubject<Menu[]>(this.MENUITEMS);
}
